import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StatusSteps } from 'common/utils/types';
import { repeatDropDownList, beforeDropDownList } from 'features/UploadDocument/content';
import { SmartScanResponse } from 'features/UploadDocument/types';
import { createCategoryKey } from 'common/utils/categoryUtils';
import { DEFAULT_DOCUMENT_NAME } from 'features/Onboarding/constants';
import {
  User, DefaultUpload, CategoryType,
} from './types';

export type CounterState = {
  header: string;
  user: User;
  uploadDocument: DefaultUpload;
}

const initialUserState: User = {
  id: null,
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  terms: false,
  token: '',
};

const initialUploadDocumentState: DefaultUpload = {
  stepNameStatus: StatusSteps.current,
  stepRenewalStatus: StatusSteps.pending,
  stepFileStatus: StatusSteps.pending,
  name: '',
  category: [],
  renewalDate: '',
  reminder: true,
  before: '',
  beforeType: beforeDropDownList[0].name,
  repeatToggle: true,
  repeat: repeatDropDownList[1].name,
  files: [],
  categoryList: [],
  dvlaResponse: [],
  returnToReview: false,
};

export const initialState: CounterState = {
  header: '',
  user: initialUserState,
  uploadDocument: initialUploadDocumentState,
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setHeader: (state, { payload }: PayloadAction<string>) => {
      state.header = payload;
    },
    setUserData: (
      state,
      { payload: { user } }: PayloadAction<{ user: User }>,
    ) => {
      state.user = { ...state.user, ...user };
    },
    setCategoryData: (state, { payload: { categoryList } }: PayloadAction<{ categoryList: CategoryType}>) => {
      state.uploadDocument = {
        ...state.uploadDocument,
        categoryList,
      };
    },
    setStepNameUploadData: (
      state,
      { payload }: PayloadAction<DefaultUpload>,
    ) => {
      state.uploadDocument = {
        ...state.uploadDocument,
        name: payload.name,
        category: payload.category,
        stepNameStatus: payload.stepNameStatus,
      };
    },
    setReturnToReview: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.uploadDocument.returnToReview = payload;
    },
    setStepVehicleData: (
      state,
      { payload }: PayloadAction<Partial<DefaultUpload>>,
    ) => {
      // Merge only the keys the caller actually passed. This action is
      // dispatched with different partial shapes at different steps of the
      // vehicle flow (e.g. VehicleDetails' "Continue" only sends
      // { vehicleId }) — unconditionally writing every field to
      // payload.<field> (undefined when absent) was wiping dvlaResponse
      // (and vehicleName/vehicleRegistration/stepVehicleStatus) that an
      // earlier step in the SAME dispatch chain had just set, which is why
      // VehicleInfoCard rendered empty on /document/review.
      state.uploadDocument = {
        ...state.uploadDocument,
        ...(payload.vehicleName !== undefined && { vehicleName: payload.vehicleName }),
        ...(payload.vehicleRegistration !== undefined && { vehicleRegistration: payload.vehicleRegistration }),
        ...(payload.stepVehicleStatus !== undefined && { stepVehicleStatus: payload.stepVehicleStatus }),
        ...(payload.dvlaResponse !== undefined && { dvlaResponse: payload.dvlaResponse }),
        ...(payload.vehicleId !== undefined && { vehicleId: payload.vehicleId }),
      };
    },
    setStepRenewalUploadData: (
      state,
      { payload }: PayloadAction<DefaultUpload>,
    ) => {
      state.uploadDocument = {
        ...state.uploadDocument,
        renewalDate: payload.renewalDate,
        reminder: payload.reminder,
        before: payload.before,
        beforeType: payload.beforeType,
        repeatToggle: payload.repeatToggle,
        repeat: payload.repeat,
        stepRenewalStatus: payload.stepRenewalStatus,
      };
    },
    setStepFileUploadData: (
      state,
      { payload }: PayloadAction<DefaultUpload>,
    ) => {
      state.uploadDocument = {
        ...state.uploadDocument,
        files: payload.files,
      };
    },
    setNotesData: (
      state,
      { payload }: PayloadAction<{ notes: string }>,
    ) => {
      state.uploadDocument = {
        ...state.uploadDocument,
        notes: payload.notes,
      };
    },
    setDataFields: (
      state,
      { payload }: PayloadAction<{ data: Array<{key: string, value: string}> }>,
    ) => {
      state.uploadDocument = {
        ...state.uploadDocument,
        dataFields: payload.data,
      };
    },
    setSmartScanData: (
      state,
      { payload }: PayloadAction<SmartScanResponse>,
    ) => {
      // Aggregate extracted data (don't replace)
      const existingData = state.uploadDocument.extractedData || [];
      const newData = payload.data.data || [];

      // Merge: add new keys, update existing keys with new values
      const mergedData = [...existingData];
      newData.forEach((newItem) => {
        const existingIndex = mergedData.findIndex(
          (existing) => existing.key.toLowerCase() === newItem.key.toLowerCase(),
        );
        if (existingIndex >= 0) {
          // Update existing key with new value
          mergedData[existingIndex] = newItem;
        } else {
          // Add new key-value pair
          mergedData.push(newItem);
        }
      });

      state.uploadDocument = {
        ...state.uploadDocument,
        extractedData: mergedData,
      };

      // Collect suggested names (accumulate unique values)
      if (payload.data.suggestedName) {
        const existingNames = state.uploadDocument.suggestedNames || [];
        if (!existingNames.includes(payload.data.suggestedName)) {
          state.uploadDocument.suggestedNames = [...existingNames, payload.data.suggestedName];
        }
        // Auto-fill name unless the user has already typed something real.
        // Treat both "truly empty" and "still the untouched default" as
        // unset — scan-success now skips NameItemScreen entirely, so this is
        // the only place left that can promote the suggestion onto the
        // working copy before doc-detail renders it.
        if (!state.uploadDocument.name || state.uploadDocument.name === DEFAULT_DOCUMENT_NAME) {
          state.uploadDocument.name = payload.data.suggestedName;
        }
      }

      // Set renewal data if present. The AI-scanned renewal object is not
      // guaranteed to be complete at runtime (a truthy-but-partial or empty
      // object was reaching here and throwing on renewal.before.toString()),
      // so every field is guarded and only written when actually present.
      // Missing fields are left untouched (state defaults), and a complete
      // renewal object still writes every field exactly as before.
      if (payload.data.renewal) {
        const { renewal } = payload.data;
        if (renewal.renewalDate != null) {
          state.uploadDocument.suggestedRenewalDate = renewal.renewalDate;
          state.uploadDocument.renewalDate = renewal.renewalDate;
        }
        if (renewal.reminder != null) {
          state.uploadDocument.reminder = renewal.reminder;
        }
        if (renewal.before != null) {
          state.uploadDocument.before = renewal.before.toString();
        }
        if (renewal.beforeType != null) {
          state.uploadDocument.beforeType = renewal.beforeType;
        }
        if (renewal.repeatToggle != null) {
          state.uploadDocument.repeatToggle = renewal.repeatToggle;
        }
        if (renewal.repeat != null) {
          state.uploadDocument.repeat = renewal.repeat;
        }
      }

      // Set suggested category if present (only if no category already selected)
      if (payload.data.suggestedCategory && state.uploadDocument.category.length === 0) {
        state.uploadDocument.suggestedCategory = payload.data.suggestedCategory;
        // Auto-select the suggested category (convert to CategoryKey format)
        const isCustom = payload.data.suggestedCategory.isCustom || false;
        state.uploadDocument.category = [createCategoryKey(payload.data.suggestedCategory.id, isCustom)];
      }
    },
    resetUploadData: (
      state,
    ) => {
      state.uploadDocument = {
        ...initialUploadDocumentState,
      };
    },
    // Applies the user's saved default reminder lead time (Notifications
    // settings) on top of a freshly reset upload draft. Dispatched right
    // after resetUploadData() at "start a new upload" entry points only
    // (AddDoc, DesktopHeader), not from every reset call site, and not
    // from the reducer's own initial state, so this stays a targeted
    // override rather than a change to what a fresh upload looks like by
    // default. A later scan result still wins: setSmartScanData only
    // writes before/beforeType when the scan actually detected a value.
    seedDefaultReminderLeadTime: (
      state,
      { payload }: PayloadAction<{ before: number; beforeType: string }>,
    ) => {
      // Invariant: redux's uploadDocument.beforeType is pre-normalized to
      // Days/Months only ('Weeks' is UI sugar - content.ts gives it the
      // Days id, and only RenewalDate.tsx's submit collapses Weeks to
      // Days x 7; see the comment at DocumentReview.tsx:419-425). The
      // profile's saved default can still be 'Weeks' verbatim, so convert
      // it here before it lands on the draft - otherwise a scan that skips
      // RenewalDate (FastScanLoader -> DocumentReview) would save "2 Weeks
      // before" as before_type Days with count 2 ("2 days before").
      if (payload.beforeType === 'Weeks') {
        state.uploadDocument.before = String(payload.before * 7);
        state.uploadDocument.beforeType = 'Days';
      } else {
        state.uploadDocument.before = String(payload.before);
        state.uploadDocument.beforeType = payload.beforeType;
      }
    },
    // Clears everything a previous file's scan (and the user's edits to it)
    // wrote, without touching files/categoryList/step state. Needed when the
    // user goes back and picks a DIFFERENT file: setSmartScanData deliberately
    // merges (multi-file batches in UploadFile.tsx aggregate per-file scans),
    // and the whole app slice is redux-persisted, so without an explicit reset
    // the old file's fields/names/category bleed into the new scan.
    resetScanData: (
      state,
    ) => {
      state.uploadDocument = {
        ...state.uploadDocument,
        name: initialUploadDocumentState.name,
        category: [],
        renewalDate: initialUploadDocumentState.renewalDate,
        reminder: initialUploadDocumentState.reminder,
        before: initialUploadDocumentState.before,
        beforeType: initialUploadDocumentState.beforeType,
        repeatToggle: initialUploadDocumentState.repeatToggle,
        repeat: initialUploadDocumentState.repeat,
        extractedData: undefined,
        dataFields: undefined,
        suggestedNames: undefined,
        suggestedCategory: undefined,
        suggestedRenewalDate: undefined,
        notes: undefined,
      };
    },
    setStepPropertyData: (
      state,
      { payload }: PayloadAction<{ propertyId?: number; propertyName?: string }>,
    ) => {
      state.uploadDocument = {
        ...state.uploadDocument,
        propertyId: payload.propertyId,
        propertyName: payload.propertyName,
      };
    },
    // Generic group selection for the non-vehicle/property group-capable
    // categories. Stores the picked group as {slug, id, name}; DocumentReview
    // translates the slug → doc FK column via groupRegistry at submit time.
    setStepGroupData: (
      state,
      { payload }: PayloadAction<{ groupSlug?: string; groupId?: number; groupName?: string }>,
    ) => {
      state.uploadDocument = {
        ...state.uploadDocument,
        groupSlug: payload.groupSlug,
        groupId: payload.groupId,
        groupName: payload.groupName,
      };
    },
  },
});
export const {
  setUserData,
  setStepNameUploadData,
  setStepVehicleData,
  setStepRenewalUploadData,
  setStepFileUploadData,
  setCategoryData,
  setNotesData,
  setDataFields,
  setSmartScanData,
  resetUploadData,
  seedDefaultReminderLeadTime,
  resetScanData,
  setStepPropertyData,
  setStepGroupData,
  setReturnToReview,
} = appSlice.actions;

export default appSlice.reducer;
